import { useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Divider,
  MenuItem,
  MenuList,
  Popover,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logOut, selectUser } from "@/app/features/auth/authSlice";
import { persistor } from "@/app/store";
import { useLogoutMutation } from "@/app/features/auth/authApiSlice";
import { apiSlice } from "@/app/api/apiSlice";

export const AccountPopover = (props) => {
  const { anchorEl, onClose, open } = props;

  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const [logoutMutation] = useLogoutMutation();

  const handleSignOut = useCallback(async () => {
    try {
      // Call logout API endpoint
      await logoutMutation().unwrap();
    } catch (error) {
      // Continue with logout even if API call fails
      console.error("Logout API error:", error);
    } finally {
      // Clear RTK Query cache first
      dispatch(apiSlice.util.resetApiState());
      
      // Clear Redux state
      dispatch(logOut());
      
      // Clear redux-persist storage
      await persistor.purge();
      
      // Clear all localStorage
      localStorage.clear();
      
      // Clear all sessionStorage
      sessionStorage.clear();
      
      // Close the popover
      onClose();
      
      // Redirect to login page
      navigate("/login", { replace: true });
    }
  }, [dispatch, navigate, logoutMutation, onClose]);

  const handleChangePassword = useCallback(() => {
    onClose();
    navigate("/account/change-password");
  }, [navigate, onClose]);

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: "left",
        vertical: "bottom",
      }}
      onClose={onClose}
      open={open}
      PaperProps={{ sx: { width: 220 } }}
    >
      <Box
        sx={{
          py: 1.5,
          px: 2,
        }}
      >
        <Typography variant="overline">Account</Typography>
        <Typography color="text.secondary" variant="body2">
          {user.name}
        </Typography>
      </Box>
      <Divider />
      <MenuList
        disablePadding
        dense
        sx={{
          p: "8px",
          "& > *": {
            borderRadius: 1,
          },
        }}
      >
        <MenuItem onClick={handleChangePassword}>
          <LockOutlinedIcon fontSize="small" sx={{ mr: 1.5, color: "text.secondary" }} />
          Change password
        </MenuItem>
        <MenuItem onClick={handleSignOut}>
          <LogoutOutlinedIcon fontSize="small" sx={{ mr: 1.5, color: "text.secondary" }} />
          Sign out
        </MenuItem>
      </MenuList>
    </Popover>
  );
};

AccountPopover.propTypes = {
  anchorEl: PropTypes.any,
  onClose: PropTypes.func,
  open: PropTypes.bool.isRequired,
};
